import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useTranslation } from 'react-i18next';
import AdminNavigation from './AdminNavigation';

const DRAWER_WIDTH = 240;

const AdminBackoffice: React.FC = () => {
  const { t, i18n } = useTranslation('admin');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Lazy load admin namespace when component mounts
  useEffect(() => {
    if (!i18n.hasResourceBundle(i18n.language, 'admin')) {
      i18n.loadNamespaces('admin');
    }
  }, [i18n]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer (temporary) */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          <AdminNavigation />
        </Drawer>

        {/* Desktop drawer (persistent) */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
          open
        >
          <AdminNavigation />
        </Drawer>
      </Box>

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        {/* Mobile menu toggle button - floats in top-right */}
        {isMobile && (
          <Box
            onClick={handleDrawerToggle}
            sx={{
              position: 'fixed',
              top: 70, // Below main navigation
              right: 16,
              zIndex: theme.zIndex.drawer - 1,
              bgcolor: 'background.paper',
              boxShadow: 2,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'primary.light',
                color: 'white',
              },
            }}
          >
            <MenuIcon fontSize="small" />
            <Typography variant="caption" fontWeight="bold">
              {t('backoffice.title')}
            </Typography>
          </Box>
        )}
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminBackoffice;
